import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIconButton from '../../components/DeleteIconButton';
import { Alert, Breadcrumbs, Button, Dialog, DialogContent, DialogContentText, DialogTitle, Grid, IconButton, Link, Tab, Tabs, TextField, Tooltip, Typography } from '@mui/material';
import { makeStyles, useTheme } from '@mui/styles';
import { Box } from '@mui/system';
import { useSnackbar } from 'notistack';
import * as React from 'react';
import { useHistory, useParams } from 'react-router';

import { DataAPIContext } from '../../api';
import { CREATE, GET_ONE, UPDATE } from '../../api/dataProvider';
import AccessDeniedView from '../../components/AccessDeniedView';
import FixedLinearProgress from '../../components/FixedLinearProgress';
import FormActionButtons from '../../components/FormActionButtons';
import FormLicenseeContext, { toLicenseeId } from '../../components/FormLicenseeContext';
import LicenseeFormField from '../../components/LicenseeFormField';
import RecordList from '../../components/RecordList';
import RemoteAutocomplete from '../../components/RemoteAutocomplete';
import { ReactComponent as PublishIcon } from '../../icons/publish.svg'
import { ContextStore } from '../../store/contextStore';
import { useIsWidthUp } from '../../utils';
import UsedByPanel from '../../components/UsedByPanel';
import AccessGroupEditPermissions from './AccessGroupEditPermissions';
import AccessGroupMembersListToolbar from './AccessGroupMembersListToolbar';
import { refusalText, skippedNotice } from './mcpEntitlements';
import BlockRedirect from '../../components/BlockRedirect';
import { DRAWER_WIDTH } from '../../layout/drawer';
import { a11yProps, CustomTabPanel, LEAVE_WITHOUT_SAVING_MESSAGE } from './utils';
import useConfirmDialog from '../../components/useConfirmDialog';

const useStyles = makeStyles((theme) => ({
    root: {
        padding: '1rem',
        margin: 0,
        borderLeft: 'solid 1px #d8d8d8',
        overflow: 'auto',

    },
    gridRoot: {
        [theme.breakpoints.down('sm')]: {
            paddingBottom: '2rem',
        },
    },
    recordList: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2),
        height: '250px',
        '&.MuiDataGrid-root .MuiDataGrid-cell:focus-within': {
            outline: 'none',
        },
        [theme.breakpoints.down('sm')]: {
            overflowY: 'scroll',
            height: '100%',
        },
    },
    buttonBar: {
        marginTop: theme.spacing(2),
    },
}));

const ParentLinks = (props) => {
    if (!props.data) {
        return null;
    } else {
        const parents = [];
        let cur = props.data;
        while (cur !== null) {
            parents.push(cur);
            cur = cur.parent;
        }
        return (
            <Breadcrumbs separator="›">
                <Link underline="hover" color="inherit" href="/user/permissions">
                    Root
                </Link>
                {parents.toReversed().map((x) => (
                    <Link key={x.name} underline="hover" color="inherit" href={`/user/permissions/${x.id}`}>
                        {x.name}
                    </Link>
                ))}
            </Breadcrumbs>
        );
    }
};

// const removeDuplicates = (list) => {
//     const seen = new Map();
//     const uniqueList = [];

//     for (const item of list) {
//         if (!seen.has(item.id)) {
//             seen.set(item.id, true);
//             uniqueList.push(item);
//         }
//     }

//     return uniqueList;
// }


const AccessGroupEdit = (props) => {
    const [confirmDelete, confirmDeleteDialog] = useConfirmDialog();
    const dataAPI = React.useContext(DataAPIContext);
    const urlParams = useParams();
    const history = useHistory();
    const classes = useStyles();
    const resource = props.resource;
    const { enqueueSnackbar } = useSnackbar();
    const { roles, roleMap, hasPerm } = React.useContext(ContextStore);
    const tour_user_roles = [{ id: null, name: 'Guest' }, ...roles];
    const [ag, setAg] = React.useState(props.data);
    const [addUsersDialogOpen, setAddUsersDialogOpen] = React.useState(false);
    const [lastUpdate, setLastUpdate] = React.useState(null);
    const [isLoading, setIsLoading] = React.useState(false);
    const currentTab = +urlParams.sub || 0;
    const theme = useTheme();
    const [recordListQuery, setRecordListQuery] = React.useState(null)

    const [recordListFilters, setRecordListFilters] = React.useState([])
    const isSmallScreen = !useIsWidthUp('sm');
    const hasAccessTagEditPerm = hasPerm('atag', 'write')
    const hasAccessGroupEditPerm = hasPerm('agroup', 'write')
    // Template-managed groups are maintained by TourOptima staff: everything except
    // membership is read-only in the dashboard (the API rejects writes as well).
    const isManagedGroup = Boolean(ag?.managed && ag?.template)
    const canEditGroup = hasAccessGroupEditPerm && !isManagedGroup

    const onLicenseeChange = (id) => {
        setAg((prev) => ({ ...prev, licensee: id, _changed: true }));
    };

    const [canSave, setCanSave] = React.useState(false);
    const [cacheStatus, setCacheStatus] = React.useState(null); // { stale_member_count, stale_members }
    const [cacheStatusLoading, setCacheStatusLoading] = React.useState(false);

    const [lastSavedAg, setLastSavedAg] = React.useState(props.data);

    const fetchCacheStatus = React.useCallback(() => {
        setCacheStatusLoading(true);
        dataAPI(GET_ONE, resource, {
            id: props.data.id,
            urlSuffix: 'check_cache/',
        })
            .then((res) => {
                if (res.statusCode === 200) {
                    setCacheStatus(res.data);
                } else {
                    console.error('Failed to fetch cache status', res);
                    setCacheStatus(null);
                }
            })
            .finally(() => setCacheStatusLoading(false));
    }, [dataAPI, resource, props.data.id]);

    React.useEffect(() => {
        fetchCacheStatus();
    }, [fetchCacheStatus]);

    const hasChanges = (currentAg) => {
        if (currentAg == null || lastSavedAg == null) {
            return currentAg !== lastSavedAg;
        }
        return JSON.stringify(currentAg) !== JSON.stringify(lastSavedAg);
    };

    // Fields the user edits on the General tab. Permission/APS endpoints don't change these,
    // so we keep the user's in-progress edits when those endpoints respond.
    const GENERAL_TAB_FIELDS = ['name', 'codename', 'atag', 'roles', 'propagated_roles'];

    /**
     * Apply a server access-group payload after a write that has already been persisted.
     * - For full saves (Save button), call with `preserveLocalEdits = false` to fully sync.
     * - For permission / APS writes that don't touch General-tab fields, pass `true` so the
     *   user's unsaved name / code name / role / tag edits aren't clobbered by the response.
     */
    const applyGroupFromServer = (data, { preserveLocalEdits = false } = {}) => {
        if (!data) {
            return;
        }
        if (preserveLocalEdits) {
            setAg((prev) => {
                if (!prev) {
                    return data;
                }
                const next = { ...data };
                for (const f of GENERAL_TAB_FIELDS) {
                    if (Object.prototype.hasOwnProperty.call(prev, f)) {
                        next[f] = prev[f];
                    }
                }
                return next;
            });
        } else {
            setAg(data);
        }
        setLastSavedAg(data);
    };

    React.useEffect(() => {
        if (hasChanges(ag)) {
            setCanSave(true);
        } else {
            setCanSave(false);
        }
    }, [ag, lastSavedAg]);

    // Stable identity: BlockRedirect re-arms its history.block whenever this prop changes.
    const ignoredLeavePaths = React.useMemo(() => [`/user/permissions/group/${ag.id}`], [ag.id]);

    React.useEffect(() => {
        if (currentTab === 1) {
            props.setNavigateBackBarData && props.setNavigateBackBarData({ title: "Access Group: " + ag.name, addAction: () => setAddUsersDialogOpen(true), buttonText: "Add member", goBackButtonAction: props.onClose, permission: 'agroup' })
            return;
        }
        if (currentTab === 2) {
            props.setNavigateBackBarData && props.setNavigateBackBarData({ title: "Access Group: " + ag.name, addAction: null, buttonText: null, goBackButtonAction: props.onClose })
            return;
        }
        props.setNavigateBackBarData && props.setNavigateBackBarData({ title: "Access Group: " + ag.name, addAction: null, buttonText: null, goBackButtonAction: props.onClose })
    }, [currentTab])

    const onTextChange = (ev) => {
        // Read before the updater, which React may defer past its controlled-input reset
        // (see onNewGroupTextChange in AccessGroupsList).
        const { name, value } = ev.target;
        setAg((prevState) => ({
            ...prevState,
            [name]: value,
        }));
    };

    const onAccessTagChange = (ev, selected, reason) => {
        setAg((prevState) => ({
            ...prevState,
            atag: selected,
        }));
    };

    const onEmployeeRoleChange = (ev, selected, reason) => {
        console.log('node atag change', selected);
        setAg((prevState) => ({
            ...prevState,
            roles: selected,
        }));
    };

    const onPropagatedRoleChange = (ev, selected, reason) => {
        console.log('node atag change', selected);
        setAg((prevState) => ({
            ...prevState,
            propagated_roles: selected,
        }));
    };

    const onAddUsersDialogClose = () => {
        setAddUsersDialogOpen(false);
    };

    const onAddNewUser = (ev, user, reason) => {
        setIsLoading(true);
        console.log('node: add', user);
        dataAPI(CREATE, resource, {
            urlSuffix: `${props.data.id}/add_user/`,
            data: { users: [user.id] },
            // a 403 here is the backend's answer to this request, not an expired session
            keepSessionOn403: true,
        })
            .then((res) => {
                if (res.statusCode === 200 || res.statusCode === 201 || res.statusCode === 204) {
                    console.log('node: got add user res', res);
                    props.onAccessGroupUpdate && props.onAccessGroupUpdate(res.data);
                    setLastUpdate(new Date().toString());
                    if ([200, 204].indexOf(res.statusCode) >= 0) {
                        enqueueSnackbar(`User ${user.email} added successfully`, { variant: 'success' });
                    } else if (res.statusCode === 201) {
                        enqueueSnackbar(`User ${user.email} is scheduled to be added in a few moments`, { variant: 'success' });
                    }
                }
                else{
                    if (res.statusCode === 500) {
                        enqueueSnackbar("Error: Please try again in a few minutes (500)", { variant: 'error', autoHideDuration: 5000 });
                        return;
                    }
                    if (res?.data?.errors?.[0]?.message || res?.data?.message) {
                        enqueueSnackbar(`Error: ${res?.data?.errors?.[0]?.message || res?.data?.message} (${res.statusCode})`, { variant: 'error', autoHideDuration: 5000 });
                        return;
                    }
                }
            })
            .finally(() => setIsLoading(false));
    };

    const onDeleteUser = async (user) => {
        if (await confirmDelete({ body: `Are you sure you want to remove user ${user.display_name} from this access group?`, okButtonText: 'Remove', destructive: true })) {
            setIsLoading(true);
            dataAPI(CREATE, resource, {
                urlSuffix: `${props.data.id}/remove_user/`,
                data: { users: [user.id] },
            }).then((res) => {
                if (res.statusCode === 200 || res.statusCode === 201 || res.statusCode === 204) {
                    console.log('node: got remove user res', res);
                    props.onAccessGroupUpdate && props.onAccessGroupUpdate(res.data);
                    setLastUpdate(new Date().toString());
                    if ([200, 204].indexOf(res.statusCode) >= 0) {
                        enqueueSnackbar(`User ${user.email} removed successfully`, { variant: 'success' });
                    } else if (res.statusCode === 201) {
                        enqueueSnackbar(`User ${user.email} is scheduled to be removed in a few moments`, { variant: 'success' });
                    }
                }
                else{
                    enqueueSnackbar(`${res?.data?.errors?.[0]?.message || "Error. Please try again in a few minutes"}`, { variant: 'error', autoHideDuration: 5000 });
                }
            }).catch((error) => {
                enqueueSnackbar(`${error}`, { variant: 'error' });
            }).finally(() => setIsLoading(false));
        }
    };

    // What a batch did not apply, one line each (./mcpEntitlements.js): the actions the backend skipped -- unknown,
    // guest-forbidden, franchise-scope-forbidden, an MCP permission the granter does not hold (Q9,
    // explicit_grant_required) -- and the ones the page left out before sending (`leftOut`).
    const showBatchNotice = (skipped, leftOut) => {
        const notice = skippedNotice(skipped, leftOut);
        if (!notice) return;
        const message = <div>{notice.lines.map((line) => <div key={line}>{line}</div>)}</div>;
        enqueueSnackbar(message, { variant: notice.variant, autoHideDuration: 10000 });
    };

    const onBatchPermissions = (actions, { leftOut = [] } = {}) => {
        if (!actions || actions.length === 0) {
            showBatchNotice([], leftOut);
            return;
        }
        setIsLoading(true);
        dataAPI(CREATE, resource, {
            urlSuffix: `${props.data.id}/batch_permissions/`,
            data: { actions },
            // a refusal is an answer here, not an expired session
            keepSessionOn403: true,
        })
            .then((res) => {
                if (res.statusCode === 200 || res.statusCode === 201) {
                    applyGroupFromServer(res.data, { preserveLocalEdits: true });
                    // Backend reports actions it refused instead of failing the whole batch.
                    showBatchNotice(res.data?.skipped || [], leftOut);
                } else {
                    enqueueSnackbar(`${res?.data?.message || res?.data?.errors?.[0]?.message || "Error applying permission changes. Please try again in a few minutes"}`, { variant: 'error', autoHideDuration: 5000 });
                }
            })
            .finally(() => setIsLoading(false));
    };

    const onAddPermission = (scope, action) => {
        const perm = { scope, action };
        setIsLoading(true);
        dataAPI(CREATE, resource, {
            urlSuffix: `${props.data.id}/add_permission/`,
            data: { permission: perm },
            // 403 explicit_grant_required (Q9) is an answer, not an expired session
            keepSessionOn403: true,
        })
            .then((res) => {
                if (res.statusCode === 200 || res.statusCode === 201 || res.statusCode === 204) {
                    console.log('node add permission res', res.data);
                    applyGroupFromServer(res.data, { preserveLocalEdits: true });
                    enqueueSnackbar(`Permission ${scope}.${action} added`, { variant: 'success' });
                } else {
                    enqueueSnackbar(refusalText(res?.data, scope, action, 'add'), { variant: 'error', autoHideDuration: 8000 });
                }
            })
            .finally(() => setIsLoading(false));
    };

    const onRemovePermission = (scope, action) => {
        const perm = { scope, action };
        setIsLoading(true);
        dataAPI(CREATE, resource, {
            urlSuffix: `${props.data.id}/remove_permission/`,
            data: { permission: perm },
        })
            .then((res) => {
                if (res.statusCode === 200 || res.statusCode === 201 || res.statusCode === 204) {
                    console.log('node remove permission res', res.data);
                    applyGroupFromServer(res.data, { preserveLocalEdits: true });
                    enqueueSnackbar(`Permission ${scope}.${action} removed`, { variant: 'success' });
                }
                else {
                    enqueueSnackbar(`${res?.data?.errors?.[0]?.message || "Error. Please try again in a few minutes"}`, { variant: 'error', autoHideDuration: 5000 });
                }
            })
            .finally(() => setIsLoading(false));
    };

    const onAccessTagsChange = (aps, atags) => {
        console.log('node aps change', props.data.id, aps, atags);
        setIsLoading(true);
        dataAPI(UPDATE, resource, {
            id: props.data.id,
            urlSuffix: 'aps/',
            data: { aps_id: aps.id, atags },
            keepSessionOn403: true,
        })
            .then((res) => {
                console.log('node update aps res', res);
                if (res.statusCode === 200) {
                    applyGroupFromServer(res.data, { preserveLocalEdits: true });
                    enqueueSnackbar(`Access tags for permission ${aps.scope}.${aps.action} updated`, { variant: 'success' });
                } else {
                    enqueueSnackbar(refusalText(res?.data, aps.scope, aps.action, 'tags', `Error updating access tags for permission ${aps.scope}.${aps.action}`), { variant: 'error' });
                }
            })
            .finally(() => setIsLoading(false));
    };

    const onTargetAccessGroupsChange = (aps, target_agroups) => {
        console.log('node aps change', props.data.id, aps, target_agroups);
        setIsLoading(true);
        dataAPI(UPDATE, resource, {
            id: props.data.id,
            urlSuffix: 'aps/',
            data: { aps_id: aps.id, target_agroups },
        })
            .then((res) => {
                console.log('node update aps res', res);
                if (res.statusCode === 200) {
                    applyGroupFromServer(res.data, { preserveLocalEdits: true });
                    enqueueSnackbar(`Target access groups for permission ${aps.scope}.${aps.action} updated`, { variant: 'success' });
                } else {
                    enqueueSnackbar(res?.data?.errors?.[0]?.message || `Error updating target access groups for permission ${aps.scope}.${aps.action}: ${res.data.message}`, { variant: 'error' });
                }
            })
            .finally(() => setIsLoading(false));

    }

    const onAllTagsSwitch = (aps) => {
        console.log('yes, all tags switch', aps);
        setIsLoading(true);
        dataAPI(UPDATE, resource, {
            id: props.data.id,
            urlSuffix: 'aps/',
            data: { aps_id: aps.id, all_tags: !aps.all_tags },
            keepSessionOn403: true,
        })
            .then((res) => {
                console.log('node update aps res', res);
                if (res.statusCode === 200) {
                    applyGroupFromServer(res.data, { preserveLocalEdits: true });
                    enqueueSnackbar(`Access tags for permission ${aps.scope}.${aps.action} updated`, { variant: 'success' });
                } else {
                    enqueueSnackbar(refusalText(res?.data, aps.scope, aps.action, 'all_tags', `Error updating access tags for permission ${aps.scope}.${aps.action}`), { variant: 'error' });
                }
            })
            .finally(() => setIsLoading(false));
    };

    const onTabChange = (ev, newtab) => {
        console.log('node tab', newtab);
        // setCurrentTab(newtab);
        history.push(`/user/permissions/group/${ag.id}/${newtab}`);
    };

    const onSaveClick = (ev, refresh) => {
        dataAPI(UPDATE, resource, {
            id: ag.id,
            data: { ...ag, refresh_immediately: !!refresh },
        }).then((res) => {
            if (res.statusCode === 200) {
                const newAg = res.data;
                applyGroupFromServer(newAg);
                props.onAccessGroupUpdate && props.onAccessGroupUpdate(newAg);
                enqueueSnackbar(`Access group "${newAg.name}" saved successfully`, { variant: 'success' });
            } else {
                enqueueSnackbar(`${res?.data?.message || res?.data?.errors?.[0]?.message || "Error saving access group. Please try again in a few minutes"}`, { variant: 'error', autoHideDuration: 5000 });
            }
        });
    };

    const onSaveAndRefreshClick = (ev) => {
        return onSaveClick(ev, true);
    }

    const onMemberCacheRefresh = (ev) => {
        if (window.confirm('Are you sure you want to refresh the permission cache? Note that it may take a while.')) {
            setIsLoading(true);
            dataAPI(CREATE, resource, {
                urlSuffix: `${props.data.id}/refresh_cache/`,
            })
                .then((res) => {
                    console.log('node member cache refresh res', res);
                    if (res.statusCode === 200) {
                        enqueueSnackbar(`Permission cache for members of the "${ag.name}" group initiated`, { variant: 'success' });
                        fetchCacheStatus();
                    } else {
                        enqueueSnackbar(`Error initiating permission cache refresh for group "${ag.name}"`, { variant: 'error' });
                    }
                })
                .finally(() => setIsLoading(false));
        }
    };



    if (!hasPerm('agroup', 'read')) {
        return <AccessDeniedView />
    }
    return (
        <FormLicenseeContext.Provider value={toLicenseeId(ag.licensee)}>
            {confirmDeleteDialog}
            {/* Switching tabs stays on the same group, so it is not "leaving" (CP-5575). */}
            <BlockRedirect
                shouldBlock={canSave}
                message={LEAVE_WITHOUT_SAVING_MESSAGE}
                ignoredPaths={ignoredLeavePaths}
            />
            <FixedLinearProgress visibility={isLoading} />
            <Dialog open={!!addUsersDialogOpen} onClose={onAddUsersDialogClose} maxWidth="md" fullWidth>
                {!!addUsersDialogOpen && (
                    <>
                        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, pr: 1 }}>
                            <Typography component="span" variant="h6" sx={{ flex: 1 }}>
                                Add users to group {ag.name}
                            </Typography>
                            <Tooltip title="Close">
                                <IconButton aria-label="close" onClick={onAddUsersDialogClose} size="small" sx={{ color: 'rgba(0, 0, 0, 0.87)' }}>
                                    <CloseIcon />
                                </IconButton>
                            </Tooltip>
                        </DialogTitle>
                        <DialogContent>
                            <Grid container direction="row" spacing={2}>
                                <Grid item xs={12}>
                                    <DialogContentText>Add one or more users</DialogContentText>
                                </Grid>
                                <Grid item xs={12}>
                                    <RemoteAutocomplete
                                        key={isLoading}
                                        resource="user"
                                        urlSuffix={null}
                                        label="Users"
                                        variant="standard"
                                        size="small"
                                        fullWidth
                                        onChange={onAddNewUser}
                                        filterSelected
                                        apiFilters={[{ field: 'access_group', op: 'ne', value: [ag.id] }]}
                                        getOptionLabel={(opt) => `${opt.display_name} (ID: ${opt.id}, email: ${opt.email})`}
                                        value={null}
                                        InputProps={{
                                            autoFocus: true,
                                        }}
                                    />
                                </Grid>
                            </Grid>
                        </DialogContent>
                    </>
                )}
            </Dialog>
            {isManagedGroup && (
                <Alert severity="info" sx={{ mb: 1 }}>
                    This access group is entirely managed by TourOptima staff. Its settings and
                    permissions are read-only; members can still be edited.
                </Alert>
            )}
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs value={currentTab} onChange={onTabChange}>
                    <Tab label="General" {...a11yProps(0)} />
                    <Tab label="Members" {...a11yProps(1)} />
                    <Tab label="Permissions" {...a11yProps(2)} />
                    {props.data?.id && <Tab label="Used by" {...a11yProps(3)} />}
                </Tabs>
            </Box>
            <CustomTabPanel value={currentTab} index={0} >
                <Grid container direction="row" spacing={2} className={classes.gridRoot} >
                    {ag.parent ? (
                        <Grid item xs={12}>
                            <ParentLinks data={ag.parent} />
                        </Grid>
                    ) : null}
                    <Grid item xs={12}>
                        <LicenseeFormField value={ag.licensee} onChange={onLicenseeChange} disabled={!canEditGroup} allowNone />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField variant="filled" label="Name" name="name" disabled={!canEditGroup} value={ag.name} onChange={onTextChange} fullWidth />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField variant="filled" label="Code name" name="codename" disabled={!canEditGroup} value={ag.codename} onChange={onTextChange} fullWidth />
                    </Grid>
                    <Grid item xs={12}>
                        <RemoteAutocomplete
                            multiple
                            disabled={!hasAccessTagEditPerm || isManagedGroup}
                            resource="access_tag"
                            urlSuffix={null}
                            label="Access tags"
                            size="small"
                            variant="filled"
                            fullWidth
                            onChange={onAccessTagChange}
                            filterSelected
                            value={ag.atag}
                            textFieldProps={{
                                helperText: 'These access tags control who can read or update this access group. For permissions that are granted by this access group, go to the Permissions tab.',
                            }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Button
                            variant="outlined"
                            color="primary"
                            disabled={!hasAccessGroupEditPerm || cacheStatusLoading || !cacheStatus || cacheStatus.stale_member_count === 0}
                            onClick={onMemberCacheRefresh}
                        >
                            {cacheStatusLoading ? 'Checking cache status...' : 'Refresh permissions cache for group members'}
                        </Button>
                        {cacheStatus && cacheStatus.stale_member_count > 0 && (
                            <Typography variant="body2" sx={{ color: '#d32f2f', mt: 0.5 }}>
                                {cacheStatus.stale_member_count} member{cacheStatus.stale_member_count !== 1 ? 's have' : ' has'} an out-of-date permission cache:{' '}
                                {cacheStatus.stale_members?.map((m, idx) => (
                                    <span key={m.id}>
                                        <Link href={`/user/${m.id}`} target="_blank" sx={{ color: '#d32f2f' }}>
                                            {m.display_name || m.email}
                                        </Link>
                                        {idx < cacheStatus.stale_members.length - 1 ? ', ' : ''}
                                    </span>
                                ))}
                                {cacheStatus.auto_refresh_triggered
                                    ? ' An automatic refresh has been queued; this should resolve itself within a few minutes.'
                                    : ' A refresh was already queued recently.'}
                            </Typography>
                        )}
                        {cacheStatus && cacheStatus.stale_member_count === 0 && (
                            <Typography variant="body2" sx={{ color: '#2e7d32', mt: 0.5 }}>
                                All members' permission caches are up to date.
                            </Typography>
                        )}
                        <Typography variant="body2" sx={{ color: '#888888', mt: 0.5 }}>
                            Out-of-date member caches are now detected and refreshed automatically, and members also self-heal on their next request — this button is only a manual fallback.
                            Time needed to refresh depends on the size of the group: nearly instant for most groups, but groups with thousands of users may take a while.
                        </Typography>
                    </Grid>
                </Grid>
            </CustomTabPanel>
            {currentTab === 1 && <>
                <AccessGroupMembersListToolbar
                    resource={resource}
                    setLastUpdate={setLastUpdate}
                    tourUserRoles={tour_user_roles}
                    setRecordListFilters={setRecordListFilters}
                    recordListFilters={recordListFilters}
                    recordListQuery={recordListQuery}
                    setRecordListQuery={setRecordListQuery}
                />
                <Grid item xs={12} container direction="row" spacing={2}>
                    <Grid item xs={12} sx={{ height: isSmallScreen ? '30vh' : '50vh', marginBottom: '16px' }}>
                        <RecordList
                            checkboxSelection={false}
                            className={classes.recordList}
                            resource={'user'}
                            apiFilters={[{ field: 'access_group', value: [ag.id] }]}
                            query={recordListQuery}
                            deps={lastUpdate}
                            rowHeight={50}
                            columns={[
                                // { field: 'id', headerName: 'ID', flex: 0.25 },
                                { field: 'name', headerName: 'Name', filterable: false, sortable: true, flex: 0.75, valueGetter: (p) => p.row?.name || p.row?.display_name },
                                {
                                    field: 'roles', headerName: 'Roles', filterable: false, sortable: true, flex: 0.75,
                                    renderCell: (p) => (

                                        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'nowrap' }}>

                                            {p.row.roles.map(role => <div style={{ backgroundColor: role === 10 ? '#D8D8D8' : '#E4E4F9', padding: '0.05rem 0.5rem', height: '24px' }}><Typography fontSize={14}>{roleMap[role].name}</Typography></div>)}
                                               {!p.row.is_tour_operator_staff && <div style={{ backgroundColor: '#F8D7DA', padding: '0.05rem 0.5rem', height: '24px' }}><Typography fontSize={14}>Guest</Typography></div>}
                                        </div>
                                    ),

                                },
                                { field: 'email', headerName: 'Email', flex: 0.75 },
                                { field: 'phone', headerName: 'Phone', flex: 0.75 },
                                {
                                    field: 'actions',
                                    headerName: 'Actions',
                                    sortable: false,
                                    minWidth: 100,
                                    renderCell: (p) => (
                                        <Grid container justifyContent="flex-end" alignItems="center" spacing={1} p={1}>
                                            {/* A centered flex row: the bare icon in the link sits on the text baseline and the
                                                trash button on the middle, so on one plain line the trash rode ~8px low (Rea, CP-5545 10/18). */}
                                            <Grid item style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                                                {hasAccessGroupEditPerm &&
                                                <Tooltip title="User details">
                                                    <Link href={`/user/${p.row.id}`} target="_blank" sx={{ display: 'inline-flex' }}>
                                                        <AccountCircleIcon style={{ color: theme.palette.primary.main }} />
                                                    </Link>
                                                </Tooltip>}
                                                {/* <Tooltip title="Link user">
                                                    <LinkIcon onClick={() => onDeleteUser(p.row)} style={{ color: theme.palette.primary.main }} />
                                                </Tooltip> */}
                                                <DeleteIconButton
                                                    title="Remove user from access group"
                                                    allowed={hasAccessGroupEditPerm}
                                                    fontSize="medium"
                                                    sx={{ p: 0 }}
                                                    onClick={() => onDeleteUser(p.row)}
                                                />
                                            </Grid>
                                        </Grid>
                                    ),
                                },
                            ]}
                        />
                    </Grid>

                </Grid>
                <Grid item xs={12} p={2} >
                    <Typography variant="h6" >Roles</Typography>
                    <Typography variant="body2" maxWidth={600}>Role can be associated with access groups only to simplify migration from the old permissions model. Please do not use this field unless you know what you are doing.</Typography>
                </Grid>
                <Grid item xs={12} p={2} mb={isSmallScreen ? 5 : 0}>
                    <RemoteAutocomplete
                        multiple
                        resource="employee_role"
                        urlSuffix={null}
                        style={{ maxWidth: '400px' }}
                        label="Role"
                        variant="filled"
                        size="small"
                        disabled={!canEditGroup}
                        fullWidth
                        onChange={onEmployeeRoleChange}
                        filterSelected
                        value={ag.roles} />
                </Grid>
                <Grid item xs={12} p={2} >
                    <Typography variant="h6" >Propagated roles</Typography>
                    <Typography variant="body2" maxWidth={600}>If staff users are added to the access group, these roles will be assigned to them. This is mainly needed for backwards compatibility and smooth transition from old roles to access groups.</Typography>
                </Grid>
                <Grid item xs={12} p={2} mb={isSmallScreen ? 5 : 0}>
                    <RemoteAutocomplete
                        multiple
                        resource="employee_role"
                        urlSuffix={null}
                        style={{ maxWidth: '400px' }}
                        label="Propagated role"
                        variant="filled"
                        size="small"
                        disabled={!canEditGroup || JSON.parse(window.localStorage.getItem('bootstrap'))?.user['email'] !== "help@curatedplanet.com"}
                        fullWidth
                        onChange={onPropagatedRoleChange}
                        filterSelected
                        value={ag.propagated_roles} />
                </Grid>
            </>}
            {currentTab === 2 &&
                <div style={{ paddingBottom: isSmallScreen ? '2rem' : 0 }}>
                    <AccessGroupEditPermissions
                        resource={props.resource}
                        readOnly={isManagedGroup}
                        data={ag}
                        permissionList={props.permissionList}
                        permissionMap={props.permissionMap}
                        onAddPermission={onAddPermission}
                        onRemovePermission={onRemovePermission}
                        onAccessTagsChange={onAccessTagsChange}
                        onTargetAccessGroupsChange={onTargetAccessGroupsChange}
                        onAllTagsSwitch={onAllTagsSwitch}
                        onBatchPermissions={onBatchPermissions}
                        originalPermissions={props.data?.permissions}
                    />
                </div>}
            {currentTab === 3 && props.data?.id &&
                <Box sx={{ p: 3 }}>
                    <UsedByPanel resource="access_group" id={props.data.id} />
                </Box>}
            {/* CP-8423: [CANCEL] [SAVE & APPLY NOW] [SAVE] bottom-right ("apply now" = refresh members' permissions immediately). left/right size the fixed bar;
                width: 100% on top of the drawer offset pushed right-aligned buttons off-screen. */}
            <Box style={{ position: 'fixed', bottom: 0, backgroundColor: '#F5F5F5', right: 0, left: isSmallScreen ? 0 : DRAWER_WIDTH, padding: '1rem', zIndex: 100 }}>
                <FormActionButtons
                    mode="edit"
                    onSubmit={onSaveClick}
                    submitDisabled={!canSave || isManagedGroup}
                    onCancel={props.onClose}
                    extraActions={(
                        <Button variant="outlined" size="small" color="primary" style={{ backgroundColor: 'white' }} disabled={!canSave || isManagedGroup} onClick={onSaveAndRefreshClick} startIcon={<PublishIcon />}>
                            Save & apply now
                        </Button>
                    )}
                />
            </Box>
        </FormLicenseeContext.Provider>
    );
};

export default AccessGroupEdit;

